import { FieldArray, FormikErrors } from 'formik';
import { Plus, Trash2 } from 'lucide-react';

import { PathSelector } from '@/react/portainer/gitops/ComposePathField/PathSelector';
import { GitFormModel } from '@/react/portainer/gitops/types';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Button } from '@@/buttons';
import { ExternalLink } from '@@/ExternalLink';
import { FormError } from '@@/form-components/FormError';

import { FormValues } from '../types';

interface Props {
  valuesFiles: Array<string>;
  gitModel: GitFormModel;
  errors: FormikErrors<FormValues> | Record<string, unknown>;
  onValuesFileChange: (index: number, value: string) => void;
  variant?: 'create' | 'edit';
  stackID?: number;
  fromEdgeStack?: boolean;
}

export function HelmValuesFilesSection({
  valuesFiles,
  gitModel,
  errors,
  onValuesFileChange,
  variant = 'create',
  stackID,
  fromEdgeStack,
}: Props) {
  const valuesFilesContent = (
    <FieldArray name="helmValuesFiles">
      {({ push, remove }) => (
        <div>
          {valuesFiles.map((file, index) => (
            <div
              key={`helm-values-file-${index}`}
              className={variant === 'edit' ? 'mb-2 relative z-[90]' : 'mb-2'}
              style={variant === 'edit' ? { zIndex: 90 - index } : undefined}
            >
              <div className="flex gap-2 align-top items-start">
                <div className="flex-1">
                  <PathSelector
                    value={file}
                    onChange={(value) => onValuesFileChange(index, value)}
                    placeholder="e.g. values/production.yaml"
                    model={gitModel}
                    dirOnly={false}
                    inputId={`helm-values-file-${index}`}
                    stackID={stackID}
                    fromEdgeStack={fromEdgeStack}
                    hasError={
                      !!(
                        errors.helmValuesFiles &&
                        Array.isArray(errors.helmValuesFiles) &&
                        errors.helmValuesFiles[index]
                      )
                    }
                  />
                </div>
                <Button
                  type="button"
                  color="dangerlight"
                  className="h-[34px]"
                  data-cy={`helm-values-file-remove-${index}`}
                  onClick={() => remove(index)}
                  icon={Trash2}
                  size="medium"
                >
                  <span className="sr-only">Remove</span>
                </Button>
              </div>
              {errors.helmValuesFiles &&
                Array.isArray(errors.helmValuesFiles) &&
                errors.helmValuesFiles[index] && (
                  <FormError>
                    {typeof errors.helmValuesFiles[index] === 'string'
                      ? errors.helmValuesFiles[index]
                      : ''}
                  </FormError>
                )}
            </div>
          ))}
          <Button
            type="button"
            color="secondary"
            size="small"
            onClick={() => push('')}
            data-cy="helm-values-file-add"
            icon={Plus}
            className="!ml-0"
          >
            Add values file
          </Button>
          {variant === 'edit' && (
            <span className="help-block mt-2">
              Specify user-defined values files to override the default chart
              values. Paths are relative to the Helm chart folder in your Git
              repository.
            </span>
          )}
        </div>
      )}
    </FieldArray>
  );

  if (variant === 'edit') {
    return <>{valuesFilesContent}</>;
  }

  return (
    <FormSection title="Install options">
      <FormControl
        label="Values files"
        tooltip={
          <>
            <p>
              Specify user-defined values files to override the default chart
              values.
            </p>
            <p>
              If multiple values files are specified, they will be merged in the
              order they are specified (last values file takes precedence).
            </p>
            <p>
              You can get more information about the Helm values file format in
              the{' '}
              <ExternalLink
                to="https://helm.sh/docs/chart_template_guide/values_files/"
                data-cy="helm-values-file-format-link"
              >
                official documentation
              </ExternalLink>
              .
            </p>
          </>
        }
      >
        {valuesFilesContent}
      </FormControl>
    </FormSection>
  );
}
